import { ChatHistorySidebarBase, type SidebarSession } from "@/components/shared/ChatHistorySidebarBase"
import { useTenderChatStore } from "@/stores/tender-chat-store"

interface TenderChatHistorySidebarProps {
  isExpanded: boolean
  onExpandedChange: (expanded: boolean) => void
  /** Fill the host (the assistant dock's history view) instead of a right-hand rail. */
  fill?: boolean
  /** Called after a conversation is chosen or a new one started (the dock returns to the chat). */
  onSelected?: () => void
}

/**
 * Tender chat history = agent THREADS within the bid's chat session. Each row
 * can be renamed, pinned/unpinned and deleted (delete is confirm-gated inside
 * the base component); labels come from the thread name or, until a thread is
 * named, the first-message preview.
 */
export function TenderChatHistorySidebar({ isExpanded, onExpandedChange, fill = false, onSelected }: TenderChatHistorySidebarProps) {
  const {
    sessionId,
    error,
    threads,
    activeThreadId,
    threadPreviews,
    startNewChat,
    selectThread,
    renameThread,
    togglePinThread,
    deleteThread,
  } = useTenderChatStore()

  // No session yet means the bid's first fetch has not settled (switching bids
  // resets it): the thread list arrives with the session. A failed fetch ends
  // the wait, so the spinner never hangs.
  const isListLoading = sessionId === null && error === null

  const sessions: SidebarSession[] = threads.map((thread) => ({
    id: thread.id,
    title: threadTitle(thread.name, threadPreviews[thread.id]),
    updatedAt: thread.updatedAt,
    createdAt: thread.createdAt,
    userId: thread.createdBy,
    organizationId: '',
    messageCount: 0,
    lastMessageAt: null,
    isPinned: thread.isPinned,
  }))

  return (
    <ChatHistorySidebarBase
      isExpanded={isExpanded}
      onExpandedChange={onExpandedChange}
      sessions={sessions}
      currentSessionId={activeThreadId}
      onNewChat={() => {
        void startNewChat()
        onSelected?.()
      }}
      onSelectSession={(threadId) => {
        void selectThread(threadId)
        onSelected?.()
      }}
      onRenameSession={(threadId, newTitle) => renameThread(threadId, newTitle)}
      onTogglePin={(threadId) => togglePinThread(threadId)}
      onDeleteSession={(threadId) => deleteThread(threadId)}
      headerTitle="Conversations"
      emptyText="No conversations yet"
      emptyActionText="Start a new chat"
      isLoading={isListLoading}
      renameDialogTitle="Rename conversation"
      renameDialogDescription="Give this conversation a name you can find later."
      renameInputLabel="Name"
      renameInputPlaceholder="e.g. Pricing brainstorm"
      fill={fill}
    />
  )
}

/** Prefer the stored thread name; fall back to the first-message preview. */
function threadTitle(name: string, preview: string | undefined): string {
  const trimmedName = name?.trim()
  if (trimmedName) return trimmedName
  const trimmedPreview = preview?.trim()
  if (trimmedPreview) return trimmedPreview
  return 'New chat'
}
